<template>
  <v-radio-group
    :id="snInputProps_identifier"
    v-model="response"
    :data-cy="snInputProps_label + '-group'"
    :autofocus="snInputProps_autofocus"
    :class="snInputProps_classes"
    :error-messages="snInputProps_errorMessages"
    :name="snInputProps_name"
    :disabled="snInputProps_disabled"
    :aria-label="snInputProps_label"
    :hide-details="snInputProps_hideDetails"
    @blur="$emit('blur')"
    @focus="$emit('focus')"
  >
    <v-row
      v-if="!stacked"
      no-gutters
    >
      <v-flex
        v-for="(option, i) in optionObjects"
        :key="i"
        xs0_75
        :class="response === option.value ? 'radio-selected' : ''"
        @click="changeValue(option.value)"
      >
        <v-radio
          :id="`${snInputProps_identifier}-${i}`"
          :class="`sel_radio_${snInputProps_identifier}_${option.label ? option.label.toLowerCase() : undefined}`"
          :data-cy="`${snInputProps_identifier}-${option.label}` + '-btn'"
          :aria-label="option.label"
          :label="option.text"
          :value="option.value"
          off-icon="$snRadio"
          on-icon="$snRadioSelected"
        />
      </v-flex>
    </v-row>
    <v-row
      v-for="(option, i) in optionObjects"
      v-else
      :key="i"
      no-gutters
    >
      <v-flex
        xs0_75
        :class="response === option.value ? 'radio-selected' : ''"
        class="stacked"
        @click="changeValue(option.value)"
      >
        <v-icon
          v-if="option.icon"
          class="sn-radio-icon"
          style="color: #1F1F1F !important;"
        >
          {{ option.icon }}
        </v-icon>
        <v-radio
          :id="`${snInputProps_identifier}-${i}`"
          :class="`sel_radio_${snInputProps_identifier}_${option.label ? option.label.toLowerCase() : undefined} ${option.icon ? `radio-icon` : undefined }`"
          :data-cy="option.label + '-btn'"
          :aria-label="option.label"
          :label="option.text"
          :value="option.value"
          off-icon="$snRadio"
          on-icon="$snRadioSelected"
        />
      </v-flex>
    </v-row>
  </v-radio-group>
</template>

<script>
import SnInputProps from '../../props/SnInputProps'
import deprecatedMixin from '~/components/snui/v2/_deprecated/deprecatedMixin.vue'

export default {
  name: 'SnRadio',
  mixins: [deprecatedMixin],
  props: {
    ...SnInputProps,
    // Options are an array of objects that have a Name and Value assigned
    options: {
      type: Array,
      default: () => [],
      required: true
    },
    stacked: {
      type: Boolean,
      default: false,
      required: false
    },
    value: {
      type: [String, Number, Array, Boolean],
      default: '',
      required: false
    },
    defaultToFirstOption: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      response: '',
      optionObjects: [],
      deprecatedMixin: {
        alternativeComponent: 'SnRadioGroup'
      }
    }
  },
  watch: {
    value (newVal) {
      this.response = newVal
    },
    response (newVal) {
      this.$emit('input', this.response)
    },
    options (newOptions) {
      this.setOptionObjects(newOptions)
    }
  },
  mounted () {
    this.response = this.value
    this.setOptionObjects(this.options)
    if (this.defaultToFirstOption && this.optionObjects?.[0]?.value) {
      this.response = this.optionObjects[0].value
    }
  },
  methods: {
    changeValue (value) {
      this.response = value
      this.$emit('change', value)
    },
    setOptionObjects (options) {
      if (Array.isArray(options) && options.length > 0 && typeof options[0] !== 'object') {
        const optionObjects = []
        options.map((option) => {
          if (option !== '') {
            optionObjects.push({
              label: option,
              text: option,
              value: option
            })
          }
        })
        this.optionObjects = optionObjects
      } else if (Array.isArray(options)) {
        this.optionObjects = options
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';

::v-deep.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.v-input--is-disabled {
  ::v-deep .v-input__slot {
    .flex {
      background-color: $sn-gray-01;
      border: 1px solid $sn-gray-04;
      color: $sn-primary-01;

      &:not(:first-child) {
        border-left: 1px solid $sn-gray-04;
      }
      .v-radio {
        .v-label {
          color: $sn-primary-01 !important;
        }
      }
    }
  }
}

::v-deep {
  font-family: $font-family-primary !important;

  .v-input__slot {
    .flex {
      border: 1px solid $sn-gray-04;
      box-sizing: border-box;
      border-radius: 2px;
      min-height: 72px !important;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor:pointer;
      background-color: $sn-white;

      &.radio-selected {
        border: 1px solid $sn-brand !important;
        background: $sn-brand-01 !important;
        z-index: 4;

        .error--text {
          color: $sn-brand !important;

          .v-icon {
            color: $sn-brand !important;
          }
        }

        .v-icon {
          color: $sn-brand !important;
        }

        &:hover {
          border: 1px solid $sn-brand !important;
          box-shadow: none;

          .v-icon {
            color: $sn-brand !important;
          }
        }
      }

      &.stacked {
        margin-bottom: 8px;
        margin-left: 0px;
        position: relative;

        .v-radio {
          > div {
            position: absolute;
            right: 10px;
          }

          > label {
            position: absolute !important;
            padding-left: 20px !important;
            height: 100%;
            width: 100%;
          }
        }
      }

      &:not(:first-child) {
        margin-left: -1px;
      }

      &:focus-within {
        border: 1px solid $sn-primary !important;
      }

      &:hover {
        border: 1px solid $sn-primary !important;
        box-shadow: $sn-shadow-depth-1;
        z-index: 4;

        .v-icon {
          color: $sn-primary !important;
        }
      }

      .v-icon {
        color: $sn-gray-04 !important;
      }

      .v-radio {
        .v-label {
          color: $sn-primary !important;
          font-size: 1.063em !important;
        }

        .v-input--selection-controls__input {
          &:hover {
            .v-input--selection-controls__ripple {
              &:before {
                background: none;
              }
            }
          }
        }
      }
    }
  }

  &.error--text {
    .v-input__slot {
      .flex {
        border: 1px solid #ff5252 !important;
      }
    }
  }

  .v-messages {
    color: $sn-primary-01;

    .v-messages__message {
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 20px;
    }

    &.error--text {
      font-size: 15px;
      display: inline-flex;
      align-items: center;

      &:before {
        content: '\26A0';
        margin-right: 5px;
        font-size: 15px;
        margin-bottom: 22px;
      }

      .v-messages__wrapper {
        display: inline-flex;
        padding-bottom: 2px;
      }
    }
  }
  .radio-icon {
   label {
     left: 25px !important;
   }
  }
  .sn-radio-icon {
    position: absolute;
    left: 10px;
  }
}
</style>
